// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import deepEqual from 'react-fast-compare';
import {
  objectEmpty,
  intersection,
  isEmpty,
  isNotEmpty
} from 'utils/functional';

import {
  stateSearchFilters,
  serializeFiltersForSavedSearch
} from 'utils/search/searchFacets';
import {
  makeSavedSearchLocation,
  filterToIndex
} from 'utils/search/savedSearches';
import { radiusStringToInt } from 'utils/search/radius';
import { LISTING_TYPE_PLURAL_HUMAN_NAME_MAP } from 'utils/constants';
import { sprintf } from 'sprintf-js';
import type { SavedSearchUnsaved } from 'api/currentUser';
import { saveSearch } from '../redux/main/actions/currentUser';
import { showModal } from '../redux/main/actions/modal';
import { searchAutosaveDisable } from '../redux/main/actions/mainSearch';
import useTemporaryStorage from '../utils/useTemporaryStorage';
import { getParam } from '../utils/url';

const { useState } = React;

const filterIncludesOrgPostedActions = filters =>
  filters.actionTypeFacet &&
  (filters.actionTypeFacet === 'ALL' ||
    (Array.isArray(filters.actionTypeFacet) &&
      !isEmpty(intersection(filters.actionTypeFacet, ['VOLOP', 'EVENT']))));

const getSavedSearchKeys = (savedSearch: SavedSearchUnsaved) => {
  const {
    text,
    filters,
    location: {
      geoType,
      stateCode,
      countryCode,
      latitude,
      longitude,
      boundingBoxN,
      boundingBoxS,
      boundingBoxW,
      boundingBoxE
    },
    radius
  } = savedSearch;
  const savedSearchKeys = {
    text,
    filters,
    geoType,
    stateCode,
    countryCode,
    // Need to round the floats to prevent inconsistency
    latitude: latitude && parseFloat(latitude.toFixed(4)),
    longitude: longitude && parseFloat(longitude.toFixed(4)),
    boundingBoxN: boundingBoxN && parseFloat(boundingBoxN.toFixed(4)),
    boundingBoxS: boundingBoxS && parseFloat(boundingBoxS.toFixed(4)),
    boundingBoxW: boundingBoxW && parseFloat(boundingBoxW.toFixed(4)),
    boundingBoxE: boundingBoxE && parseFloat(boundingBoxE.toFixed(4)),
    radius
  };
  return savedSearchKeys;
};

const searchesAreEqual = (s1: SavedSearchUnsaved, s2: SavedSearchUnsaved) =>
  deepEqual(getSavedSearchKeys(s1), getSavedSearchKeys(s2));

type OwnProps = {
  autosaveSearch?: boolean,
  render: ({
    saveSearch: () => void,
    currentSearchIsSaved: boolean,
    saveableSearch: boolean
  }) => React.Node
};

type Props = {
  ...$Exact<OwnProps>,
  query: string,
  filters: SearchFiltersWithType<ListingType>,
  location: Object,
  radius: ?string,
  searchFacets: { [string]: SearchFacet },
  currentUser: Object,
  saveSearch: Function,
  showModal: typeof showModal,
  searchAutosaveDisable: () => void,
  tmpId: ?string
};

const SaveSearchContainer = (props: Props) => {
  const [autosaveApplied, setAutosaveApplied] = useState(false);

  const searchFromState = () => {
    const { text, ...locationWithoutText } = props.location;
    return {
      filters: stateSearchFilters(props.searchFacets, props.filters),
      location: {
        location: text,
        ...locationWithoutText
      },
      text: props.query,
      radius: radiusStringToInt(props.radius),
      index: filterToIndex(props.filters.type)
    };
  };

  const showEmailSignupModal = (tmpId: string) => {
    const locationText = props.location.text
      ? sprintf(getText('in %(locationText)s'), {
          locationText: props.location.text
        })
      : getText('on Idealist');
    const listingTypeText =
      LISTING_TYPE_PLURAL_HUMAN_NAME_MAP[props.filters.type || 'ALL'];
    const redirectTo = `${window.location.pathname}${window.location.search}${
      window.location.search ? '&' : '?'
    }tmpId=${tmpId}`;
    props.showModal('EMAIL_SIGNUP', {
      title: sprintf(
        getText('Get email alerts about %(listingTypeText)s %(locationText)s'),
        { listingTypeText, locationText }
      ),
      redirectTo
    });
  };

  const currentSearchIsSaved = () => {
    if (!props.currentUser.id) {
      return false;
    }
    return (
      props.currentUser.savedSearches.find(s => {
        const searchState = searchFromState();
        return searchesAreEqual(s, {
          ...searchState,
          index: filterToIndex(props.filters.type),
          filters: serializeFiltersForSavedSearch({
            filters: props.filters,
            searchFacets: props.searchFacets
          })
        });
      }) !== undefined
    );
  };

  const [saveTemporaryStorage] = useTemporaryStorage<
    $Exact<SavedSearchUnsaved>
  >(currentSearchIsSaved() ? '' : props.tmpId);

  const saveSearchInternal = (autosave?: boolean) => {
    const { currentUser } = props;

    if (autosave) {
      if (autosaveApplied) {
        return;
      }
      props.searchAutosaveDisable();
      setAutosaveApplied(true);
    }
    const search = {
      text: props.query,
      filters: serializeFiltersForSavedSearch({
        searchFacets: props.searchFacets,
        filters: props.filters
      }),

      radius: radiusStringToInt(props.radius),
      location: makeSavedSearchLocation(props.currentUser.searchLocation),
      index: filterToIndex(props.filters.type)
    };

    const loggedInVerified = !!(
      currentUser &&
      currentUser.email !== null &&
      currentUser.emailVerified
    );

    const informationRequired =
      currentUser.id &&
      (!currentUser.firstName || !currentUser.lastName || !currentUser.email);

    if (loggedInVerified && !informationRequired) {
      props.saveSearch(search);
    } else if (!loggedInVerified) {
      saveTemporaryStorage({ SAVE_SEARCH: search }).then(tmpId =>
        showEmailSignupModal(tmpId)
      );
    }
  };

  // If query and filters are blank then dont allow them to save the search
  const hasFilters =
    isNotEmpty(props.query) ||
    !objectEmpty(stateSearchFilters(props.searchFacets, props.filters));
  // Cannot be only imported results
  const orgPosted =
    props.filters.type === 'VOLOP'
      ? filterIncludesOrgPostedActions(props.filters)
      : true;

  return props.render({
    saveSearch: saveSearchInternal.bind(this),
    currentSearchIsSaved: currentSearchIsSaved(),
    saveableSearch: Boolean(hasFilters && orgPosted)
  });
};

const mapStateToProps = ({
  mainSearch: { query, filters, searchFacets },
  searchRadius: { value },
  currentUser
}) => ({
  query,
  filters,
  location: currentUser.searchLocation,
  radius: value,
  searchFacets,
  currentUser,
  tmpId: getParam(window.location, 'tmpId')
});

export default connect<Props, OwnProps, _, _, _, _>(
  mapStateToProps,
  { saveSearch, showModal, searchAutosaveDisable }
)(SaveSearchContainer);
